/**
 * Componente para exibir KPIs da fila de campanhas
 * 
 * Mostra estatísticas em tempo real: pendentes, processando,
 * completos, falhas e latência média
 */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Clock, 
  Loader2, 
  CheckCircle2, 
  XCircle,
  Zap
} from 'lucide-react';
import { useCampaignQueueStats } from '@/hooks/useCampaignQueueStats';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface CampaignQueueStatsProps {
  className?: string;
}

export function CampaignQueueStats({ className }: CampaignQueueStatsProps) {
  // Obtém o tenant atual do contexto de autenticação
  const { currentTenant } = useAuth();
  
  // Passa o tenant_id para o hook para isolamento multi-tenant
  const { data: stats, isLoading, error } = useCampaignQueueStats(currentTenant?.id || null);
  
  // Se não há dados na fila, não exibe o componente
  if (!isLoading && stats && 
      stats.pending_count === 0 && 
      stats.processing_count === 0 && 
      stats.completed_count === 0 && 
      stats.failed_count === 0) {
    return null;
  }
  
  if (error) {
    return null; // Silenciosamente não exibe em caso de erro
  }
  
  // Formata latência para exibição
  const formatLatency = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };
  
  // Cards de estatísticas
  const statCards = [
    {
      label: 'Na Fila',
      value: stats?.pending_count || 0,
      icon: Clock,
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
    {
      label: 'Processando',
      value: stats?.processing_count || 0,
      icon: Loader2,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      animate: true,
    },
    {
      label: 'Enviados (24h)',
      value: stats?.completed_count || 0,
      icon: CheckCircle2,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      label: 'Falhas (24h)',
      value: stats?.failed_count || 0,
      icon: XCircle,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
    },
    {
      label: 'Latência Média',
      value: formatLatency(stats?.avg_latency_ms || 0),
      icon: Zap,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      isText: true,
    },
  ];
  
  return (
    <Card className={cn('border-dashed', className)}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="outline" className="text-xs">
            Fila de Processamento
          </Badge>
          {(stats?.pending_count || 0) > 0 || (stats?.processing_count || 0) > 0 ? (
            <Badge variant="secondary" className="text-xs animate-pulse">
              <span className="relative flex h-2 w-2 mr-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
              </span>
              Ativo
            </Badge>
          ) : null}
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {isLoading ? (
            // Skeleton loading
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
                <Skeleton className="h-8 w-8 rounded-md" />
                <div className="space-y-1">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-5 w-8" />
                </div>
              </div>
            ))
          ) : (
            statCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.label}
                  className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
                >
                  <div className={cn('p-2 rounded-md', card.bgColor)}>
                    <Icon 
                      className={cn(
                        'h-4 w-4', 
                        card.color,
                        card.animate && 'animate-spin'
                      )} 
                    />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{card.label}</p>
                    <p className="text-sm font-semibold">
                      {card.isText ? card.value : Number(card.value).toLocaleString('pt-BR')}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}
